import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { map } from "rxjs/operators";

import { environment } from "src/environments/environment";
import { HttpService } from "../core/http.service";
import { GlobalsService } from "../globals.service";

import { Transfer } from "src/app/models/services/audience";
import {
  ShareAudience,
  TransferShareAudience,
  TransferShareBusiness,
  TransferShareItem,
  TransferShareSavedGroup,
  UpdateSharedAudienceRequest
} from "src/app/models/services/transfer-share-audience.model";

interface TransferShareAudienceApiResponse {
  transferId?: string | number;
  platformId?: string | number;
  groups?: Partial<TransferShareSavedGroup>[];
  businesses?: Partial<TransferShareBusiness>[];
}

@Injectable({ providedIn: "root" })
export class TransferShareAudienceService {
  private readonly baseUrl =
    `${environment.audiencesServicesApiBaseUri}/audiences/transfers/share-audience`;

  private readonly sharedBaseUrl =
    `${environment.audiencesServicesApiBaseUri}/audiences/transfers/shared-audience`;

  constructor(
    private httpService: HttpService,
    private globalsService: GlobalsService
  ) {}

  public getShareAudience(
    transfer: Transfer,
    platformId: string | number
  ): Observable<TransferShareAudience> {
    return this.loadShareAudience(
      this.baseUrl,
      transfer,
      platformId,
      false
    );
  }

  public getSharedAudience(
    transfer: Transfer,
    platformId: string | number
  ): Observable<TransferShareAudience> {
    return this.loadShareAudience(
      this.sharedBaseUrl,
      transfer,
      platformId,
      true
    );
  }

  public saveShareAudience(
    request: UpdateSharedAudienceRequest
  ): Observable<any> {
    if (!request) {
      return throwError(new Error("request is required"));
    }

    if (
      request.platformId === null ||
      request.platformId === undefined ||
      request.platformId === ""
    ) {
      return throwError(new Error("platformId is required"));
    }

    if (
      request.transferId === null ||
      request.transferId === undefined ||
      request.transferId === ""
    ) {
      return throwError(new Error("transferId is required"));
    }

    if (
      request.audienceId === null ||
      request.audienceId === undefined ||
      request.audienceId === ""
    ) {
      return throwError(new Error("audienceId is required"));
    }

    if (!request.items) {
      return throwError(new Error("items are required"));
    }

    const companyId = this.getCompanyId();
    if (companyId === null) {
      return throwError(new Error("active company id is required"));
    }

    const payload: UpdateSharedAudienceRequest = {
      platformId: request.platformId,
      transferId: request.transferId,
      audienceId: request.audienceId,
      items: (request.items || []).map((item) => ({
        id: item.id,
        name: item.name,
        businessId: item.businessId,
        businessName: item.businessName
      }))
    };

    return this.httpService.putNoResponse(
      this.baseUrl,
      payload,
      companyId
    );
  }

  public getShareAudienceForTransfer(
    transfer: Transfer
  ): Observable<ShareAudience[]> {
    if (!transfer) {
      return throwError(new Error("transfer is required"));
    }

    const platformId = this.getPlatformId(transfer);
    if (!platformId) {
      return throwError(new Error("platformId is required"));
    }

    return this.getShareAudience(transfer, platformId).pipe(
      map((data: TransferShareAudience) => this.toAvailableItems(data))
    );
  }

  public getSharedAudienceForTransfer(
    transfer: Transfer
  ): Observable<ShareAudience[]> {
    if (!transfer) {
      return throwError(new Error("transfer is required"));
    }

    const platformId = this.getPlatformId(transfer);
    if (!platformId) {
      return throwError(new Error("platformId is required"));
    }

    return this.getSharedAudience(transfer, platformId).pipe(
      map((data: TransferShareAudience) => this.toSelectedItems(data))
    );
  }

  private loadShareAudience(
    url: string,
    transfer: Transfer,
    platformId: string | number,
    markItemsAsSelected: boolean
  ): Observable<TransferShareAudience> {
    const validationError = this.validateTransferAndPlatform(transfer, platformId);
    if (validationError) {
      return throwError(validationError);
    }

    const numericPlatformId = Number(platformId);

    if (isNaN(numericPlatformId) || numericPlatformId <= 0) {
      return throwError(new Error("platformId must be a valid number"));
    }

    const companyId = this.getCompanyId();
    if (companyId === null) {
      return throwError(new Error("active company id is required"));
    }

    return this.httpService
      .post<TransferShareAudienceApiResponse>(
        url,
        {
          transfer,
          platformId: numericPlatformId
        },
        companyId
      )
      .pipe(
        map((response: TransferShareAudienceApiResponse) =>
          this.normalizeShareAudience(
            response,
            transfer,
            numericPlatformId,
            markItemsAsSelected
          )
        )
      );
  }

  private getCompanyId(): number | null {
    const companyId = this.globalsService.getActiveCompany()?.id;

    if (companyId === null || companyId === undefined) {
      return null;
    }

    const parsedCompanyId = Number(companyId);

    if (isNaN(parsedCompanyId)) {
      return null;
    }

    return parsedCompanyId;
  }

  private validateTransferAndPlatform(
    transfer: Transfer,
    platformId: string | number
  ): Error | null {
    if (!transfer) {
      return new Error("transfer is required");
    }

    if (platformId === null || platformId === undefined || platformId === "") {
      return new Error("platformId is required");
    }

    return null;
  }

  private normalizeShareAudience(
    response: TransferShareAudienceApiResponse,
    transfer: Transfer,
    platformId: string | number,
    markItemsAsSelected: boolean
  ): TransferShareAudience {
    const resolvedPlatformId =
      response?.platformId !== null && response?.platformId !== undefined
        ? response.platformId
        : platformId;

    return {
      transferId:
        response?.transferId ||
        (transfer as any)?.id ||
        (transfer as any)?.transferId,

      platformId: resolvedPlatformId,

      platforms: [
        {
          id: resolvedPlatformId,
          name: "",
          type: "platform",
          info: undefined,
          businesses: (response?.businesses || []).map(
            (business: Partial<TransferShareBusiness>) =>
              this.normalizeBusiness(
                business,
                resolvedPlatformId,
                markItemsAsSelected
              )
          ),
          groups: (response?.groups || []).map(
            (group: Partial<TransferShareSavedGroup>) =>
              this.normalizeSavedGroup(
                group,
                resolvedPlatformId,
                markItemsAsSelected
              )
          )
        }
      ]
    };
  }

  private normalizeSavedGroup(
    group: Partial<TransferShareSavedGroup>,
    fallbackParentId?: string | number,
    markItemsAsSelected: boolean = false
  ): TransferShareSavedGroup {
    return {
      ...group,
      id: group.id as string | number,
      name: group.name || "",
      type: "saved-group",
      parentId:
        group.parentId !== null && group.parentId !== undefined
          ? group.parentId
          : fallbackParentId,
      info: group.info,
      businesses: (group.businesses || []).map(
        (business: Partial<TransferShareBusiness>) =>
          this.normalizeBusiness(
            business,
            group.id,
            markItemsAsSelected
          )
      )
    };
  }

  private normalizeBusiness(
    business: Partial<TransferShareBusiness>,
    fallbackParentId?: string | number,
    markItemsAsSelected: boolean = false
  ): TransferShareBusiness {
    return {
      ...business,
      id: business.id as string | number,
      name: business.name || "",
      type: "business",
      parentId:
        business.parentId !== null && business.parentId !== undefined
          ? business.parentId
          : fallbackParentId,
      info: business.info,
      items: (business.items || []).map((item: Partial<TransferShareItem>) =>
        this.normalizeItem(item, business.id, markItemsAsSelected)
      )
    };
  }

  private normalizeItem(
    item: Partial<TransferShareItem>,
    fallbackParentId?: string | number,
    markAsSelected: boolean = false
  ): TransferShareItem {
    return {
      ...item,
      id: item.id as string | number,
      name: item.name || "",
      type: item.type === "account" ? "account" : "advertiser",
      parentId:
        item.parentId !== null && item.parentId !== undefined
          ? item.parentId
          : fallbackParentId,
      info: item.info,
      selected: markAsSelected || item.selected === true
    };
  }

  private getPlatformId(transfer: Transfer): number | null {
    const anyTransfer = transfer as any;

    if (
      anyTransfer?.targetPlatformId !== null &&
      anyTransfer?.targetPlatformId !== undefined
    ) {
      const value = Number(anyTransfer.targetPlatformId);
      return isNaN(value) ? null : value;
    }

    if (
      anyTransfer?.targetPlatform !== null &&
      anyTransfer?.targetPlatform !== undefined
    ) {
      const value = Number(anyTransfer.targetPlatform);
      return isNaN(value) ? null : value;
    }

    return null;
  }

  private toAvailableItems(data: TransferShareAudience): ShareAudience[] {
    const items: ShareAudience[] = [];

    for (const platform of data.platforms || []) {
      if (platform.name && platform.name.trim()) {
        items.push({
          id: platform.id,
          name: platform.name,
          type: "platform",
          info: platform.info
        });
      }

      for (const business of platform.businesses || []) {
        items.push({
          id: business.id,
          name: business.name,
          type: "business",
          parentId:
            business.parentId !== null && business.parentId !== undefined
              ? business.parentId
              : platform.id,
          info: business.info
        });

        for (const item of business.items || []) {
          items.push({
            id: item.id,
            name: item.name,
            type: item.type,
            parentId:
              item.parentId !== null && item.parentId !== undefined
                ? item.parentId
                : business.id,
            info: item.info
          });
        }
      }

      for (const group of platform.groups || []) {
        items.push({
          id: group.id,
          name: group.name,
          type: "saved-group",
          parentId:
            group.parentId !== null && group.parentId !== undefined
              ? group.parentId
              : platform.id,
          info: group.info
        });

        for (const business of group.businesses || []) {
          items.push({
            id: business.id,
            name: business.name,
            type: "business",
            parentId:
              business.parentId !== null && business.parentId !== undefined
                ? business.parentId
                : group.id,
            info: business.info
          });

          for (const item of business.items || []) {
            items.push({
              id: item.id,
              name: item.name,
              type: item.type,
              parentId:
                item.parentId !== null && item.parentId !== undefined
                  ? item.parentId
                  : business.id,
              info: item.info
            });
          }
        }
      }
    }

    return this.sortAndDedupeItems(items);
  }

  private toSelectedItems(data: TransferShareAudience): ShareAudience[] {
    const items: ShareAudience[] = [];

    for (const platform of data.platforms || []) {
      for (const business of platform.businesses || []) {
        for (const item of business.items || []) {
          if (item.selected === true) {
            items.push({
              id: item.id,
              name: item.name,
              type: item.type,
              parentId:
                item.parentId !== null && item.parentId !== undefined
                  ? item.parentId
                  : business.id,
              info: item.info
            });
          }
        }
      }

      for (const group of platform.groups || []) {
        for (const business of group.businesses || []) {
          for (const item of business.items || []) {
            if (item.selected === true) {
              items.push({
                id: item.id,
                name: item.name,
                type: item.type,
                parentId:
                  item.parentId !== null && item.parentId !== undefined
                    ? item.parentId
                    : business.id,
                info: item.info
              });
            }
          }
        }
      }
    }

    return this.sortAndDedupeItems(items);
  }

  private sortAndDedupeItems(items: ShareAudience[]): ShareAudience[] {
    const seen = new Set<string>();

    return (items || [])
      .filter((item: ShareAudience) => {
        if (!item || item.id === null || item.id === undefined) {
          return false;
        }

        const key = `${item.type}:${String(item.id)}`;

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      })
      .sort((a: ShareAudience, b: ShareAudience) => {
        const aRank = this.getTypeRank(a.type);
        const bRank = this.getTypeRank(b.type);

        if (aRank !== bRank) {
          return aRank - bRank;
        }

        return (a.name || "").localeCompare(b.name || "");
      });
  }

  private getTypeRank(type: ShareAudience["type"]): number {
    switch (type) {
      case "platform":
        return 0;
      case "saved-group":
        return 1;
      case "business":
        return 2;
      case "advertiser":
      case "account":
        return 3;
      default:
        return 99;
    }
  }
}